import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useToast } from '@/contexts/ToastContext';
import { dashboardsService } from '@/services/dashboards.service';
import type { EquiplexSnapshotResponse } from '@/types/dashboards';

/** Enquanto a réplica é lida, a tela consulta o estado a cada 5 s. */
const INTERVALO_POLLING_MS = 5000;

type ErroApi = { error?: string | { errors?: { recorte?: string | string[] } }; message?: string };

const dadosDoErro = (e: unknown): ErroApi | undefined =>
  axios.isAxiosError(e) ? (e.response?.data as ErroApi | undefined) : undefined;

/** O 400 do filtro (`{"error": {"errors": {"recorte": [...]}}}`): a mensagem vai para a barra de filtros. */
function erroDoFiltro(e: unknown): string | null {
  const erro = dadosDoErro(e)?.error;
  const recorte = typeof erro === 'object' ? erro?.errors?.recorte : undefined;
  if (!recorte) return null;
  return Array.isArray(recorte) ? recorte[0] : recorte;
}

/** Sem filtro: referência estável, senão o efeito de carga reinicia a cada render. */
const SEM_PARAMS: Record<string, string> = {};

export interface EstadoPainelEquiplex<P> {
  resposta: EquiplexSnapshotResponse<P> | null;
  carregando: boolean;
  erro: string | null;
  atualizando: boolean;
  disparando: boolean;
  carregar: () => Promise<void>;
  atualizarAgora: () => Promise<void>;
  /** Montando a aba para um filtro novo — o conteúdo anterior segue na tela. */
  recalculando: boolean;
  /** Filtro recusado pelo servidor (ex.: período inteiro antes dos dados guardados). */
  erroFiltro: string | null;
}

/**
 * Estado de uma aba do painel da Equiplex: carga do snapshot, "Atualizar agora"
 * (ADMIN, atualiza todas as abas) e polling enquanto o worker lê a réplica. O toast de
 * fim sai da **transição** atualizando → parado, então aparece mesmo se o worker
 * terminar antes do primeiro polling.
 *
 * `buscar` precisa ser estável (função de módulo), senão o efeito de carga reinicia; `params`
 * é o filtro da página (`useFiltroPaineis`), que já chega com referência estável.
 */
export function usePainelEquiplex<P>(
  buscar: (params: Record<string, string>) => Promise<EquiplexSnapshotResponse<P>>,
  params: Record<string, string> = SEM_PARAMS,
): EstadoPainelEquiplex<P> {
  const { showToast } = useToast();
  const [resposta, setResposta] = useState<EquiplexSnapshotResponse<P> | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [erroFiltro, setErroFiltro] = useState<string | null>(null);
  const [recalculando, setRecalculando] = useState(false);
  const [disparando, setDisparando] = useState(false);
  const estavaAtualizando = useRef(false);
  // Troca de filtro rápida: só a resposta do último pedido vale.
  const ultimoPedido = useRef(0);
  const temResposta = useRef(false);

  const aceitar = useCallback((nova: EquiplexSnapshotResponse<P>) => {
    temResposta.current = true;
    setResposta(nova);
    setErro(null);
  }, []);

  /**
   * Filtro recusado (ex.: link velho com período antes dos dados): a mensagem vai para a
   * barra de filtros. Se a tela ainda não tem nada, carrega o padrão da aba — senão a
   * pessoa ficaria sem a barra para desfazer o filtro.
   */
  const tratarErro = useCallback(
    async (e: unknown, vigente: () => boolean) => {
      const doFiltro = erroDoFiltro(e);
      if (!doFiltro) {
        setErro(dadosDoErro(e)?.message ?? 'Não foi possível carregar o painel.');
        return;
      }
      setErroFiltro(doFiltro);
      if (temResposta.current) return;
      try {
        const padrao = await buscar(SEM_PARAMS);
        if (vigente()) aceitar(padrao);
      } catch (e2) {
        if (vigente()) setErro(dadosDoErro(e2)?.message ?? 'Não foi possível carregar o painel.');
      }
    },
    [buscar, aceitar],
  );

  const carregar = useCallback(async () => {
    const pedido = ++ultimoPedido.current;
    const vigente = () => pedido === ultimoPedido.current;
    try {
      const nova = await buscar(params);
      if (!vigente()) return;
      aceitar(nova);
      setErroFiltro(null);
    } catch (e) {
      if (vigente()) await tratarErro(e, vigente);
    } finally {
      if (vigente()) setCarregando(false);
    }
  }, [buscar, params, aceitar, tratarErro]);

  useEffect(() => {
    let vigente = true;
    setRecalculando(true);
    carregar().finally(() => {
      if (vigente) setRecalculando(false);
    });
    return () => {
      vigente = false;
    };
  }, [carregar]);

  const atualizando = !!resposta?.atualizando;

  useEffect(() => {
    if (!atualizando) return undefined;
    const id = window.setInterval(carregar, INTERVALO_POLLING_MS);
    return () => window.clearInterval(id);
  }, [atualizando, carregar]);

  useEffect(() => {
    if (!resposta) return;
    if (estavaAtualizando.current && !resposta.atualizando) {
      const status = resposta.ultima_execucao?.status;
      if (status === 'SUCCESS') showToast('Painel atualizado com os dados da réplica.', 'success');
      if (status === 'FAILED') showToast('A atualização falhou. O painel segue com o último snapshot bom.', 'error');
    }
    estavaAtualizando.current = resposta.atualizando;
  }, [resposta, showToast]);

  const atualizarAgora = useCallback(async () => {
    setDisparando(true);
    try {
      await dashboardsService.refreshEquiplex();
      showToast('Atualização iniciada — leva alguns minutos.', 'info');
      estavaAtualizando.current = true;
      await carregar();
    } catch (e) {
      const data = dadosDoErro(e);
      if (data?.error === 'atualizacao_em_andamento') {
        showToast(data.message ?? 'Já existe uma atualização em andamento.', 'info');
        estavaAtualizando.current = true;
        await carregar();
      } else {
        showToast(data?.message ?? 'Não foi possível iniciar a atualização.', 'error');
      }
    } finally {
      setDisparando(false);
    }
  }, [carregar, showToast]);

  return { resposta, carregando, erro, atualizando, disparando, carregar, atualizarAgora, recalculando, erroFiltro };
}
